import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CaretRight, House, DotsThree } from '@phosphor-icons/react';
import type { BreadcrumbItemDto } from '@monorepo/shared';
import { truncatePath } from './breadcrumbTruncation';

interface DocumentBreadcrumbProps {
  items: BreadcrumbItemDto[];
  onNavigate: (nodeId: string | null) => void;
}

export const DocumentBreadcrumb = ({ items, onNavigate }: DocumentBreadcrumbProps) => {
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { isTruncated: shouldTruncate, hidden: hiddenItems } = truncatePath(items, 1, 2);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  const handleNavigate = (id: string) => {
    setIsDropdownOpen(false);
    onNavigate(id);
  };

  const separator = <CaretRight size={14} className="text-gray-400 shrink-0" />;

  const renderClickableItem = (item: BreadcrumbItemDto) => (
    <button
      key={item.id}
      onClick={() => handleNavigate(item.id)}
      className="text-gray-500 hover:text-primary transition-colors truncate max-w-[140px]"
    >
      {item.name}
    </button>
  );

  const renderCurrentItem = (item: BreadcrumbItemDto) => (
    <span key={item.id} className="text-gray-900 font-medium truncate max-w-[200px]">
      {item.name}
    </span>
  );

  return (
    <nav className="flex items-center gap-1 text-sm min-w-0">
      <button
        onClick={() => onNavigate(null)}
        className="flex items-center gap-1 text-gray-500 hover:text-primary transition-colors shrink-0"
      >
        <House size={16} />
        <span>{t('documents.breadcrumb.root')}</span>
      </button>

      {shouldTruncate ? (
        <>
          {separator}
          {renderClickableItem(items[0])}

          {separator}
          <div className="relative shrink-0" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen((prev) => !prev)}
              aria-label={t('documents.breadcrumb.show_more')}
              className="flex items-center justify-center w-6 h-6 rounded text-gray-500 hover:bg-gray-100 hover:text-primary transition-colors"
            >
              <DotsThree size={18} weight="bold" />
            </button>

            {isDropdownOpen && (
              <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-10 min-w-[160px]">
                {hiddenItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleNavigate(item.id)}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition truncate"
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {items.slice(-2).map((item, index) => (
            <div key={item.id} className="flex items-center gap-1 min-w-0">
              {separator}
              {index === 1 ? renderCurrentItem(item) : renderClickableItem(item)}
            </div>
          ))}
        </>
      ) : (
        <>
          {items.map((item, index) => (
            <div key={item.id} className="flex items-center gap-1 min-w-0">
              {separator}
              {index === items.length - 1 ? renderCurrentItem(item) : renderClickableItem(item)}
            </div>
          ))}
        </>
      )}
    </nav>
  );
};
